import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useAuth } from "react-oidc-context";

import decodeJwt from "@/utils/decodeJwt";

type Jwt = {
  roles?: {
    [service: string]: string[];
  };
  abac?: {
    [key: string]: string[];
  };
  amr?: string[]; // Authentication Methods References (e.g., "sms", "otp")
};

type JwtContextValue = {
  jwt: Jwt | null;
  hasPermission: (service: string, permission: string) => boolean;
};

const JwtContext = createContext<JwtContextValue | undefined>(undefined);

export const JwtProvider = ({ children }: WithChildren) => {
  const { user } = useAuth();

  const jwt = useMemo<Jwt | null>(
    () => (user?.access_token ? decodeJwt(user.access_token) : null),
    [user]
  );

  const hasPermission = useCallback(
    (service: string, permission: string) => {
      if (!jwt?.roles) return false;
      return jwt.roles[service]?.includes(permission) ?? false;
    },
    [jwt]
  );

  // Memoized context value (render-stability.md Rule 3) so JwtProvider re-renders
  // (e.g. on OIDC token refresh) don't churn every useJwt() consumer.
  const value = useMemo<JwtContextValue>(
    () => ({ jwt, hasPermission }),
    [jwt, hasPermission]
  );

  return <JwtContext.Provider value={value}>{children}</JwtContext.Provider>;
};

export default JwtProvider;

export const useJwt = () => {
  const context = useContext(JwtContext);
  if (!context) {
    throw new Error("useJwt must be used within a JwtProvider");
  }
  return context;
};
